import { put, takeLatest, select } from 'redux-saga/effects';
import { PayloadAction } from 'redux-starter-kit';

import {
  AboutTypes,
  MatchEndType,
  Mutations,
  Requests,
  ResolverResponse,
  Resources,
  AppPermissions,
} from '@erecruit/types';
import { openBrowserWindow } from '@erecruit/core';
import { getResourceApi } from '@erecruit/modules';
import {
  MatchRecordResponse,
  MatchRecordSections,
  ResumeData,
  MatchResumeResponse,
  Details,
  Attachments,
  Credentials,
  MatchInfoSections,
  MatchInfoResponse,
  SaveMatchRecordPayloadProps,
} from '../types';
import { withStatusInfo } from '../../ActionStatus/helpers';

import {
  approveApprovalPathFail,
  approveApprovalPathSuccess,
  downloadAllAttachmentsFail,
  downloadAllAttachmentsSuccess,
  fetchAttachmentsFail,
  fetchAttachmentsSuccess,
  fetchCredentialsFail,
  fetchCredentialsSuccess,
  fetchDetailsFail,
  fetchDetailsSuccess,
  fetchMatchInfoSuccess,
  fetchMatchRecord,
  fetchMatchRecordFail,
  fetchMatchRecordSuccess,
  fetchMatchResumeFail,
  fetchMatchResumeSuccess,
  matchActionFail,
  matchActionSuccess,
  MatchRecordState,
  rejectApprovalPathFail,
  rejectApprovalPathSuccess,
  selectors,
  saveMatchRecordFail,
} from './reducer';

const matchActionPermissions = {
  [Resources.MatchActionType.MakeOffer]: AppPermissions.MatchActionMakeOffer,
  [Resources.MatchActionType.Reject]: AppPermissions.MatchActionRejectMatch,
  [Resources.MatchActionType.InviteForInterview]:
    AppPermissions.MatchActionRequestInterview,
};

const getSections = (
  data: MatchRecordResponse,
  matchInfoSections: MatchInfoSections,
): MatchRecordSections => {
  const [
    matchSchedule,
    matchBilling,
    matchPeople,
    matchTransitions,
    matchActions,
    ,
    requirementAttachments,
    matchApprovalPath,
    matchApprovalRejectionReasons,
    matchAdditionalInfo,
  ] = data;

  return {
    ...matchInfoSections,
    schedule: matchSchedule.data[0],
    billing: matchBilling.data[0],
    people: matchPeople.data[0],
    // we want to omit statuses 'Matched' and 'Present' from match timeline
    steps: matchTransitions.data[0].steps.filter(
      ({ matchStatus: { id } }) =>
        ![
          Resources.MatchStatusType.Matched,
          Resources.MatchStatusType.Present,
        ].includes(id),
    ),
    actions: matchActions.data[0].actions.map(action => ({
      type: action,
      permission: matchActionPermissions[action],
    })),
    attachments: requirementAttachments.data[0].attachments,
    approvalPath: matchApprovalPath.data[0],
    approvalRejectionReasons: matchApprovalRejectionReasons.data,
    additionalInfo: matchAdditionalInfo.data,
  };
};

function* processFetchMatchInfo({ payload: id }: PayloadAction<number>) {
  try {
    const filter = {
      ids: [id],
    };
    const data: MatchInfoResponse = yield getResourceApi().getResources([
      new Requests.MatchRequest({ filter }),
      new Requests.MatchSearchRequest({ filter }),
    ]);

    const [matchGeneral, matchSearch] = data;
    const infoSections: MatchInfoSections = {
      general: matchGeneral.data[0],
      matchStatus: matchSearch.data[0].status,
    };

    yield put(
      fetchMatchInfoSuccess({
        infoSections,
      }),
    );
  } catch (e) {
    yield put(fetchMatchRecordFail(e.message));
  }
}

function* processFetchMatchRecord({ payload: id }: PayloadAction<number>) {
  try {
    const matchRecord: MatchRecordState = yield select(
      selectors.selectMatchRecord,
    );
    const infoSections = matchRecord.infoSections;

    if (!infoSections) {
      yield put(fetchMatchRecordFail('MatchInfo missing'));
      return;
    }

    const filter = {
      ids: [id],
    };

    const data: MatchRecordResponse = yield getResourceApi().getResources([
      new Requests.MatchScheduleRequest({ filter }),
      new Requests.MatchBillingRequest({ filter }),
      new Requests.MatchPeopleRequest({ filter }),
      new Requests.MatchTransitionsRequest({ id }),
      new Requests.MatchActionsRequest({ id }),
      new Requests.MatchEndReasonRequest({
        filter: { type: MatchEndType.Reject },
      }),
      new Requests.MatchAttachmentsRequest({ matchIds: [id] }),
      new Requests.ApprovalPathRequest({
        aboutType: AboutTypes.Match,
        referenceId: id,
      }),
      new Requests.ApprovalRejectionReasonRequest({}),
      new Requests.CustomFieldRequest({
        filter: {
          aboutObjects: [{ aboutType: AboutTypes.Match, referenceId: id }],
        },
      }),
    ]);

    const sections = getSections(data, infoSections);

    yield put(
      fetchMatchRecordSuccess({
        sections,
        rejectReasons: data[5].data,
      }),
    );
  } catch (e) {
    yield put(fetchMatchRecordFail(e.message));
  }
}

const messagesByAction = {
  [Resources.MatchActionType.MakeOffer]: {
    title: messageIds.vms.matchRecord.timeline.MakeOffer,
    progress: messageIds.vms.matchRecord.offerInterviewModal.makeOfferProgress,
    success: messageIds.vms.matchRecord.offerInterviewModal.makeOfferSuccess,
  },
  [Resources.MatchActionType.InviteForInterview]: {
    title: messageIds.vms.matchRecord.timeline.InviteForInterview,
    progress:
      messageIds.vms.matchRecord.offerInterviewModal.inviteForInterviewProgress,
    success:
      messageIds.vms.matchRecord.offerInterviewModal.inviteForInterviewSuccess,
  },
  [Resources.MatchActionType.Reject]: {
    title: messageIds.vms.matchRecord.rejectModal.title,
    progress: messageIds.vms.matchRecord.rejectModal.rejectProgress,
    success: messageIds.vms.matchRecord.rejectModal.rejectSuccess,
  },
};

type MatchActionPayloadProps = {
  matchId: number;
  matchActionType: Resources.MatchActionType;
  note?: string;
  reasonId?: number;
  matchCandidateName?: string;
};

const getHeader = ({ payload }: PayloadAction<MatchActionPayloadProps>) => ({
  title: {
    id: messagesByAction[payload.matchActionType].title,
  },
  subtitle: {
    id: messageIds.vms.general.placeholder,
    values: {
      placeholder: payload.matchCandidateName || '',
    },
  },
});

const getSuccessMessage = ({
  payload,
}: PayloadAction<MatchActionPayloadProps>) => ({
  id: messagesByAction[payload.matchActionType].success,
});

const getLoadingMessage = ({
  payload,
}: PayloadAction<MatchActionPayloadProps>) => ({
  id: messagesByAction[payload.matchActionType].progress,
});

function* matchAction({
  payload: { matchId, matchActionType, note, reasonId },
}: PayloadAction<MatchActionPayloadProps>) {
  const mutationRequest = (() => {
    switch (matchActionType) {
      case Resources.MatchActionType.MakeOffer: {
        return new Requests.MatchMakeOfferRequest({
          matchId,
        });
      }
      case Resources.MatchActionType.InviteForInterview: {
        return new Requests.MatchInviteForInterviewRequest({
          matchId,
        });
      }
      case Resources.MatchActionType.Reject: {
        return new Requests.MatchRejectRequest({
          matchId,
          note,
          reasonId: reasonId as number,
        });
      }
      default:
        throw new Error('Unsupported match action');
    }
  })();

  yield getResourceApi().createMutationRequest(mutationRequest);
  yield put(matchActionSuccess());

  yield put(fetchMatchRecord(matchId));
}

function* matchActionErrorHandler(e: Error) {
  yield put(matchActionFail(e.message));
}

function* fetchMatchResume({ payload: id }: PayloadAction<number>) {
  try {
    const filter = {
      ids: [id],
    };

    const data: MatchResumeResponse = yield getResourceApi().getResources([
      new Requests.CandidateRequest({ filter }),
      new Requests.CandidateResumeRequest({ filter }),
      new Requests.CandidateWorkHistoryRequest({ candidateIds: [id] }),
      new Requests.CandidateEducationHistoryRequest({
        candidateIds: [id],
      }),
    ]);

    const [candidate, resume, workHistory, educationHistory] = data;

    const resumeData: ResumeData = {
      candidate: candidate.success ? candidate.data[0] : undefined,
      resume: resume.success ? resume.data[0] : undefined,
      workHistory: workHistory.success
        ? workHistory.data[0]?.workHistory
        : undefined,
      educationHistory: educationHistory.success
        ? educationHistory.data[0]?.educationHistory
        : undefined,
    };

    yield put(fetchMatchResumeSuccess(resumeData));
  } catch (e) {
    yield put(fetchMatchResumeFail(e.message));
  }
}

function* fetchCredentials({ payload: id }: PayloadAction<number>) {
  try {
    const response = yield getResourceApi().getResources([
      new Requests.MatchRequirementProgressRequest({ matchId: id }),
    ]);

    yield put(
      fetchCredentialsSuccess({
        data: response[0].data as Credentials['data'],
      }),
    );
  } catch (e) {
    yield put(fetchCredentialsFail(e.message));
  }
}

function* fetchDetails({ payload: id }: PayloadAction<number>) {
  try {
    const response = yield getResourceApi().getResources([
      new Requests.RequirementDetailsRequest({ requirementIds: [id] }),
    ]) as Promise<ResolverResponse>;

    yield put(
      fetchDetailsSuccess({
        details: response[0].data as Details['details'],
      }),
    );
  } catch (e) {
    yield put(fetchDetailsFail(e.message));
  }
}

function* fetchAttachments({ payload: id }: PayloadAction<number>) {
  try {
    const response = yield getResourceApi().getResources([
      new Requests.RequirementAttachmentsRequest({ requirementIds: [id] }),
    ]) as Promise<ResolverResponse>;

    yield put(
      fetchAttachmentsSuccess({
        attachments: response[0].data[0]
          .attachments as Attachments['attachments'],
      }),
    );
  } catch (e) {
    yield put(fetchAttachmentsFail(e.message));
  }
}

function* downloadAllAttachments({
  payload: requirementIds,
}: PayloadAction<Mutations.ZipRequirementAttachmentsParams['requirementIds']>) {
  try {
    const response = yield getResourceApi().createMutationRequest(
      new Requests.ZipRequirementAttachmentsRequest({ requirementIds }),
    );

    openBrowserWindow(response[0].downloadUrl);

    yield put(downloadAllAttachmentsSuccess());
  } catch (e) {
    yield put(downloadAllAttachmentsFail(e.message));
  }
}

function* watchForFetchMatchInfo() {
  yield takeLatest('matchRecord/fetchMatchInfo', processFetchMatchInfo);
}

function* watchForFetchMatchRecord() {
  yield takeLatest('matchRecord/fetchMatchRecord', processFetchMatchRecord);
}

function* watchForCredentials() {
  yield takeLatest('matchRecord/fetchCredentials', fetchCredentials);
}

function* watchForAttachments() {
  yield takeLatest('matchRecord/fetchAttachments', fetchAttachments);
}

function* watchForDownloadAttachments() {
  yield takeLatest(
    'matchRecord/downloadAllAttachments',
    downloadAllAttachments,
  );
}

function* watchDetails() {
  yield takeLatest('matchRecord/fetchDetails', fetchDetails);
}

function* watchForMatchResume() {
  yield takeLatest('matchRecord/fetchMatchResume', fetchMatchResume);
}

function* watchForMatchAction() {
  yield takeLatest(
    'matchRecord/matchAction',
    withStatusInfo({
      successGeneratorFn: matchAction,
      errorGeneratorFn: matchActionErrorHandler,
      getHeader,
      getSuccessMessage,
      getLoadingMessage,
      autocloseMs: 1500,
    }),
  );
}

function* approveApprovalPath({
  payload: { approvalPathId, note },
}: PayloadAction<Mutations.ApproveApprovalPathParams>) {
  yield getResourceApi().createMutationRequest(
    new Requests.ApproveApprovalPathRequest({ approvalPathId, note }),
  );
  yield put(approveApprovalPathSuccess());

  const matchRecord: MatchRecordState = yield select(
    selectors.selectMatchRecord,
  );

  yield put(fetchMatchRecord(matchRecord.infoSections?.general?.id as number));
}

function* approveApprovalPathErrorHandler(e: Error) {
  yield put(approveApprovalPathFail(e.message));
}

function* watchForApproveApprovalPath() {
  yield takeLatest(
    'matchRecord/approveApprovalPath',
    withStatusInfo({
      successGeneratorFn: approveApprovalPath,
      errorGeneratorFn: approveApprovalPathErrorHandler,
      getHeader: () => ({
        title: { id: messageIds.vms.approvalPath.approve },
      }),
      getSuccessMessage: () => ({
        id: messageIds.vms.approvalPath.stepApproved,
      }),
      getLoadingMessage: () => ({
        id: messageIds.vms.approvalPath.loadingMessage,
      }),
      autocloseMs: 1500,
    }),
  );
}

function* rejectApprovalPath({
  payload: { approvalPathId, note, rejectionReasonId },
}: PayloadAction<Mutations.RejectApprovalPathParams>) {
  yield getResourceApi().createMutationRequest(
    new Requests.RejectApprovalPathRequest({
      approvalPathId,
      note,
      rejectionReasonId,
    }),
  );
  yield put(rejectApprovalPathSuccess());

  const matchRecord: MatchRecordState = yield select(
    selectors.selectMatchRecord,
  );

  yield put(fetchMatchRecord(matchRecord.infoSections?.general?.id as number));
}

function* rejectApprovalPathErrorHandler(e: Error) {
  yield put(rejectApprovalPathFail(e.message));
}

function* watchForRejectApprovalPath() {
  yield takeLatest(
    'matchRecord/rejectApprovalPath',
    withStatusInfo({
      successGeneratorFn: rejectApprovalPath,
      errorGeneratorFn: rejectApprovalPathErrorHandler,
      getHeader: () => ({
        title: { id: messageIds.vms.approvalPath.reject },
      }),
      getSuccessMessage: () => ({
        id: messageIds.vms.approvalPath.stepRejected,
      }),
      getLoadingMessage: () => ({
        id: messageIds.vms.approvalPath.loadingMessage,
      }),
      autocloseMs: 1500,
    }),
  );
}

function* saveMatchRecord(data: PayloadAction<SaveMatchRecordPayloadProps>) {
  try {
    const {
      payload: { mutation, matchId },
    } = data;
    let mutationType: string | undefined;
    let mutationParams = {};

    if (mutation.additionalInfo) {
      mutationType = nameof<Mutations.UpdateCustomField>();
      const params: Mutations.UpdateCustomFieldParams = {
        referenceId: matchId,
        aboutType: AboutTypes.Match,
        customFields: mutation.additionalInfo,
      };

      mutationParams = params;
    }

    if (!mutationType) return;

    yield getResourceApi().createMutationRequest({
      mutationType: mutationType,
      mutationParams: mutationParams,
    });

    yield put(fetchMatchRecord(matchId));
  } catch (e) {
    yield put(saveMatchRecordFail(e.message));
  }
}

function* watchForSaveMatchRecord() {
  yield takeLatest('matchRecord/saveMatchRecord', saveMatchRecord);
}

export default [
  watchForFetchMatchInfo(),
  watchForFetchMatchRecord(),
  watchForCredentials(),
  watchForAttachments(),
  watchForDownloadAttachments(),
  watchDetails(),
  watchForMatchAction(),
  watchForMatchResume(),
  watchForApproveApprovalPath(),
  watchForRejectApprovalPath(),
  watchForSaveMatchRecord(),
];
